import React, { useEffect } from "react";
import {
  Modal,
  Form,
  DatePicker,
  InputNumber,
  Typography,
  Space,
  Row,
  Col,
} from "antd";
import { CalendarOutlined, FieldTimeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { notify } from "../../../utils/toast";
import { useCreateOvertimeRequest } from "../../../query/HrmQuery";
import { IOvertimeCreate, IOvertimeRequestQuery } from "../../../models/Hrm";
import { AxiosError } from "axios";
import TextArea from "antd/es/input/TextArea";

const { Text } = Typography;

interface Props {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  query: IOvertimeRequestQuery;
}

const ModalAddOvertimeRequest: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  query,
}) => {
  const [form] = Form.useForm();
  const createOvertime = useCreateOvertimeRequest();

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      form.setFieldsValue({
        efficeDate: dayjs(),
        hour: 0,
        minute: 0,
      });
    }
  }, [isOpen, form]);

  const handleSubmit = async (values: any) => {
    try {
      const payload: IOvertimeCreate = {
        hour: values.hour || 0,
        minute: values.minute || 0,
        efficeDate: values.efficeDate.format("YYYY-MM-DD"),
        detail: values.detail,
      };

      if (payload.hour === 0 && payload.minute === 0) {
        notify.error("Vui lòng nhập thời gian tăng ca lớn hơn 0");
        return;
      }
      await createOvertime.mutateAsync({ data: payload, query });
      notify.success("Tạo đơn tăng ca thành công");
      setIsOpen(false);
      form.resetFields();
    } catch (error: unknown) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || "Tạo đơn tăng ca thất bại";
      notify.error(message);
    }
  };

  return (
    <Modal
      title={<Typography.Title level={4}>Tạo đơn xin tăng ca</Typography.Title>}
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      onOk={() => form.submit()}
      confirmLoading={createOvertime.isPending}
      okText="Gửi đơn xin tăng ca"
      cancelText="Hủy bỏ"
      width={500}
      centered
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Vui lòng điền đầy đủ thông tin bên dưới
      </Typography.Paragraph>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="efficeDate"
              label={
                <Space>
                  <CalendarOutlined className="text-blue-500" />
                  <Text strong>Ngày tăng ca</Text>
                </Space>
              }
              rules={[
                { required: true, message: "Vui lòng chọn ngày tăng ca" },
              ]}
            >
              <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="hour"
              label={
                <Space>
                  <FieldTimeOutlined className="text-blue-500" />
                  <Text strong>Số giờ</Text>
                </Space>
              }
              rules={[{ required: true, message: "Vui lòng nhập số giờ" }]}
            >
              <InputNumber
                min={0}
                max={24}
                style={{ width: "100%" }}
                addonAfter="giờ"
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="minute"
              label={
                <Space>
                  <FieldTimeOutlined className="text-blue-500" />
                  <Text strong>Số phút</Text>
                </Space>
              }
              rules={[{ required: true, message: "Vui lòng nhập số phút" }]}
            >
              <InputNumber
                min={0}
                max={59}
                style={{ width: "100%" }}
                addonAfter="phút"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="detail"
              label={
                <Space>
                  <FieldTimeOutlined className="text-blue-500" />
                  <Text strong>Lý do tăng ca</Text>
                </Space>
              }
              rules={[
                { required: true, message: "Vui lòng nhập lý do tăng ca" },
              ]}
            >
              <TextArea />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ModalAddOvertimeRequest;
